import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AccountStorageResetService {
  private readonly channelName = 'coffee-admin-account-session';
  private readonly accountDatabases = [
    'coffee-admin-conversation-messages',
    'coffee-admin-conversations',
    'coffee-admin-conversation-participants',
    'kfn-admin-service-lookups',
  ];

  private readonly resetSubject = new Subject<void>();
  readonly reset$ = this.resetSubject.asObservable();

  private resetInFlight?: Promise<void>;
  private resetChannel?: BroadcastChannel;

  constructor() {
    if (typeof BroadcastChannel === 'undefined') return;

    try {
      this.resetChannel = new BroadcastChannel(this.channelName);
      this.resetChannel.onmessage = () => {
        void this.runReset(false);
      };
    } catch {
      // Cross-tab signalling is optional; the current tab still resets fully.
    }
  }

  resetAccountData(): Promise<void> {
    return this.runReset(true);
  }

  private runReset(broadcast: boolean): Promise<void> {
    if (this.resetInFlight) return this.resetInFlight;

    this.resetSubject.next();

    // localStorage, IndexedDB and Cache Storage are already shared by tabs on
    // the same origin. A notified tab only needs to drop its own RAM/session
    // state; clearing shared stores again could race with the new login.
    if (!broadcast) {
      this.clearSessionStorage();
      return Promise.resolve();
    }

    if (broadcast) {
      try {
        this.resetChannel?.postMessage({ type: 'reset-account-data' });
      } catch {
        // The current tab reset must continue even if signalling fails.
      }
    }

    this.clearWebStorage();

    const operation = Promise.all([
      ...this.accountDatabases.map((name) => this.clearDatabase(name)),
      this.clearAuthenticatedApiCaches(),
    ])
      .then(() => undefined)
      .finally(() => {
        if (this.resetInFlight === operation) {
          this.resetInFlight = undefined;
        }
      });

    this.resetInFlight = operation;
    return operation;
  }

  private clearWebStorage(): void {
    try {
      const theme = localStorage.getItem('theme');
      localStorage.clear();

      if (theme) {
        localStorage.setItem('theme', theme);
      }
    } catch {
      // Storage can be unavailable in restricted/private browser contexts.
    }

    this.clearSessionStorage();
  }

  private clearSessionStorage(): void {
    try {
      sessionStorage.clear();
    } catch {
      // Storage can be unavailable in restricted/private browser contexts.
    }
  }

  private clearDatabase(name: string): Promise<void> {
    if (typeof indexedDB === 'undefined') return Promise.resolve();

    return new Promise<void>((resolve) => {
      let settled = false;

      const finish = () => {
        if (settled) return;

        settled = true;
        resolve();
      };

      try {
        const request = indexedDB.open(name);

        request.onupgradeneeded = (event) => {
          // Opening a missing database would create an empty v1 database and
          // could prevent its real schema from being created later.
          if ((event.oldVersion || 0) === 0) {
            request.transaction?.abort();
          }
        };

        request.onsuccess = () => {
          const db = request.result;
          const storeNames = Array.from(db.objectStoreNames);

          if (!storeNames.length) {
            db.close();
            finish();
            return;
          }

          try {
            const transaction = db.transaction(storeNames, 'readwrite');
            for (const storeName of storeNames) {
              transaction.objectStore(storeName).clear();
            }

            transaction.oncomplete = () => {
              db.close();
              finish();
            };
            transaction.onerror = () => {
              db.close();
              finish();
            };
            transaction.onabort = () => {
              db.close();
              finish();
            };
          } catch {
            db.close();
            finish();
          }
        };
        request.onerror = finish;
        request.onblocked = finish;
      } catch {
        finish();
      }
    });
  }

  private async clearAuthenticatedApiCaches(): Promise<void> {
    if (typeof caches === 'undefined') return;

    try {
      const names = await caches.keys();
      const apiCacheNames = names.filter(
        (name) => name.startsWith('ngsw:') && name.includes(':data:api:'),
      );

      await Promise.all(apiCacheNames.map((name) => caches.delete(name)));
    } catch {
      // Cache Storage is optional; the rest of the account reset still applies.
    }
  }
}
